import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {SettingsPageContainer} from '../../../../../components/Settings/SettingsPageContainer';
import {PasswordContainer} from '../../../../../components/PasswordContainer';
import {useRPC} from '../../../../../hooks/rpc';
import {WifiNetworkModel} from '../../../../../types/wifi-network';
import {ColumnModel, SettingsList} from '../../../../../components/Settings/SettingsList';
import {useMessage} from '../../../../../hooks/message';
import {useHistory, useParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {SettingReadOnlyRowComponent} from '../../../../../components/Settings/SettingReadOnlyRowComponent';
import {useLocalization} from "../../../../../hooks/localization";
import { useQuestion } from '../../../../../hooks/question';
import { setConnectedWifiAction } from '../../../../../store/settings/actions';

const BACK_URL = '/settings/gateway';

const keyExtractor = (item: WifiNetworkModel) => item.id

export const WiFiListScreen = memo(function WiFiListScreen() {
    const {t} = useLocalization();
    const params = useParams<{ id: string }>();
    const {success, failure} = useMessage();
    const [rpc] = useRPC();
    const question = useQuestion();
    const [wifiNetworkList, setWiFiNetworkList] = useState<WifiNetworkModel[]>([]);
    const [selectedWiFiNetwork, setSelectedWiFiNetwork] = useState<WifiNetworkModel>(null);
    const history = useHistory();
    const dispatch = useDispatch();

    const currentGatewayId = params.id;
    const selectedGateway = useSelector(state => state.control.gatewayList.find(g => g.id === currentGatewayId));
    const hasWifi = !(selectedGateway?.settings.home_network.is_ethernet && selectedGateway?.settings.remote_network.is_ethernet);

    const gatewayName = selectedGateway?.name;

    const refreshNetworkList = useCallback(() => {
        if (rpc == null) {
            // prevent exception if page is refreshed
            return;
        }

        // setWiFiNetworkList([{name:'sa', signal: 50, channel: '1', security:'1'}]);
        rpc.getWifiNetworkList(currentGatewayId)
            .then(networkList => {
                setWiFiNetworkList(networkList);
            })
            .catch(failure);
    }, [rpc, currentGatewayId, failure]);

    const onScanClick = useCallback(() => {
        setSelectedWiFiNetwork(null);
        setWiFiNetworkList([]);
        refreshNetworkList();
    }, [refreshNetworkList]);

    const onDisconnectPress = useCallback(() => {
        return rpc.disconnectWifiNetwork(currentGatewayId)
            .then(() => {
                success(t("disconnected"))
                refreshNetworkList()
            })
            .catch(failure)
    }, [t, failure, rpc, success, refreshNetworkList, currentGatewayId])

    const onClickOpenWifi = useCallback(() => question(t('are_you_sure'), () => rpc.wifiOn(currentGatewayId).then(success).catch(failure)), [question, success, failure, t, currentGatewayId, rpc])

    const onClickCloseWifi = useCallback(() => question(t('are_you_sure'), () => rpc.wifiOff(currentGatewayId).then(success).catch(failure)), [question, success, failure, t, currentGatewayId, rpc])

    const onClickConnect = useCallback(() => {
        dispatch(setConnectedWifiAction(selectedWiFiNetwork?.name))
        history.push(`/settings/gateway/${currentGatewayId != null ? currentGatewayId : ""}/wifi/wifi-detail`)
    }, [dispatch, history, selectedWiFiNetwork, currentGatewayId])

    const onClickConnectManual = useCallback(() => {
         dispatch(setConnectedWifiAction(''))
         history.push(`/settings/gateway/${currentGatewayId != null ? currentGatewayId : ""}/wifi/connect-manuel`)
     }, [dispatch, history, currentGatewayId])

    const buttons = useMemo(() => [
        {
            text: 'WI-FI ' + t('open').toLocaleUpperCase(),
            className: 'orange',
            onClick: onClickOpenWifi,
            hidden: !hasWifi,
        },
        {
            text: 'WI-FI ' + t('close'),
            className: 'red',
            onClick: onClickCloseWifi,
            hidden: !hasWifi,
        },
        {
            text: t('disconnect'),
            className: 'red',
            onClick: onDisconnectPress,
            hidden: !(selectedGateway?.settings?.wifi_settings?.ssid !== '') || !hasWifi,
        },
        {
            text: t('connect'),
            onClick: onClickConnect,
            className: 'blue',
            hidden: !(selectedWiFiNetwork) || !hasWifi,
        },
        {
            text: t('manual_connect'),
            onClick: onClickConnectManual,
            className: 'blue',
            hidden: !hasWifi,
        },
        {
            text: t('scan'),
            onClick: onScanClick,
            className: 'green',
            hidden: !hasWifi,
        },
    ], [t, selectedWiFiNetwork, onScanClick, onClickConnectManual, onClickConnect, selectedGateway, onDisconnectPress, hasWifi, onClickOpenWifi, onClickCloseWifi]);

    const displayNameExtractor = useCallback((item: WifiNetworkModel, column: ColumnModel) => {
        /*if (column.key === 'signal') {
            const icon = item.signal > 66 ? iconSignal3 :
                item.signal > 33 ? iconSignal2 :
                    item.signal > 0 ? iconSignal1 :
                        iconSignal0;
            return <img src={icon} alt=""/>;
        }*/
        if (column.key === 'signal_text') {
            return item.signal;
        }
        if (column.key === 'active'){
            if (item.active === 'yes'){
                return <span style={{color: '#57f016', textShadow: '0 0 8px #fff', fontSize: '17px'}}>{t('wifi_connected')}</span>
            }
            return ''
        }

        return item[column.key] || '-';
    }, [t]);

    const columns = useMemo<ColumnModel[]>(() => [
        {
            length: 2,
            name: t('signal_power'),
            key: 'signal_text',
        },
        {
            length: 3,
            name: t('wifi_name'),
            key: 'name',
        },
        {
            length: 2,
            name: t('wifi_is_connected'),
            key: 'active',
        },
        {
            length: 2,
            name: t('channel'),
            key: 'channel',
        },
       /*{
            length: 2,
            name: t('signal_power'),
            key: 'signal',
        },*/
        {
            length: 3,
            name: t('security'),
            key: 'security',
        },
    ], [t]);

    useEffect(() => {
        if (currentGatewayId == null) {
            return;
        }

        refreshNetworkList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PasswordContainer requiredRole="admin" isFullscreen={true}>
            <SettingsPageContainer
                className={hasWifi ? undefined : 'service-edit'}
                buttons={buttons}
                backUrl={BACK_URL}
                title={gatewayName || ''}>
                    {hasWifi && <span style={{color:'yellow', padding: '0.2rem', fontSize: '14px'}}>{t('wifi_warning_message')}</span>}
                {
                    renderIf(hasWifi)(() => (
                        <SettingsList
                            columns={columns}
                            items={wifiNetworkList}
                            listType='wifiNetworkList'
                            displayNameExtractor={displayNameExtractor}
                            keyExtractor={keyExtractor}
                            selectedItem={selectedWiFiNetwork}
                            onItemSelected={setSelectedWiFiNetwork}/>
                    ), () => (
                        <div className="content-divider">
                            <div className="content-wrapper">
                                <SettingReadOnlyRowComponent
                                    title={t("wifi_is_not_being_used_on_this_device")}
                                    value=" "/>
                            </div>
                        </div>
                    ))
                }
            </SettingsPageContainer>
        </PasswordContainer>
    );
});
